import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  indicatorColors, participantGroupStatusColors,
  statusLabelMap, userStatusColors,
  userStatusMap
} from '../../../../../shared/constants/common.constants';
import {CommonService} from '../../../../../shared/services/common.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ParticipantsAndCaregiversList
} from '../../../../../../models/participants-and-caregivers-list';
import {
  ParticipantAndCaregiver
} from '../../../../../../models/participantAndCaregiver';
import {GroupDashboardService} from '../../../services/group-dashboard.service';
import {
  participantsAndCaregiversList
} from '../../../core/group-dashboard.selector';
import {
  FetchParticipantsAndCaregiversListRequest
} from '../../../core/group-dashboard.actions';
import {drillDownUrls} from "../../../../../shared/core/shared.selectors";
import {SetDrillDownUrls} from "../../../../../shared/core/shared.actions";
import {MatTable, MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";

@Component({
  selector: 'app-participant-or-caregiver-information-table',
  templateUrl: './participant-or-caregiver-information-table.component.html',
  styleUrls: ['./participant-or-caregiver-information-table.component.css']
})
export class ParticipantOrCaregiverInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  loading: boolean = false;
  typeMap = {
    4: 'Adult',
    5: 'Participant',
  };
  participantCaregiverList: ParticipantsAndCaregiversList;
  displayedColumns = [
    'name',
    'role',
    'status',
    'participantsGroupStatus',
    'phoneNumber',
    'email',
    'gender',
    'pronouns',
    'ageAtStart',
    'mostRecentAttendedSession',
    'totalAttendance',
    'ethnicity',
    'preferredLanguage',
    'race',
    'govtAssist',
    'snap',
    'schoolLunchProgram',
    'foodInsecurity',
    'housingInsecurity',
    'financialStrain',
  ];
  sortColumn: any;
  sortOrder: any;
  isFirstPage = true;
  selectedUserRole: number | null;
  protected readonly clinicStatusLabelMap = statusLabelMap;
  protected readonly userStatusMap = userStatusMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private participantCaregiverFilters: {
    clientId?: number[],
    groupId?: number[],
    role?: number[],
    status?: number[],
    searchText?: string,
  } = null;

  groupId;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private groupDashboardService: GroupDashboardService,
    private router: Router,
    private route: ActivatedRoute,
  ) {

    if (this.route.snapshot.paramMap.get("groupId")) {
      this.groupId = this.route.snapshot.paramMap.get("groupId");
    }

    this.store.select(participantsAndCaregiversList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.participantCaregiverList = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }
      });

    // listen for filter changes from the filter component
    this.groupDashboardService.onParticipantCaregiverFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.participantCaregiverFilters = filters;
        this.isFirstPage = true;
        this.fetchParticipantCaregiverList();
      })
  }

  ngOnInit() {
    this.groupId = +this.route.snapshot.paramMap.get('groupId');

    // fetch participant caregiver list
    this.fetchParticipantCaregiverList();
  }

  fetchParticipantCaregiverList(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchParticipantsAndCaregiversListRequest({
      payload: {
        groupId: [this.groupId],
        ...this.participantCaregiverFilters,
        pageNumber,
        limit,
      }
    }));
  }

  onScroll() {
    if (this.participantCaregiverList?.hasNext && !this.participantCaregiverList?.loading) {
      this.fetchParticipantCaregiverList(this.participantCaregiverList.pageNumber + 1, this.participantCaregiverList.limit);
      this.isFirstPage = false;
    }
  }

  openUserDetails(user: ParticipantAndCaregiver) {
    if (user?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/member/${user.id}/behavior-tracker`]);
    }
  }

  sessionNameClick(data) {
    if (data?.latestGroupId) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/group/${data?.latestGroupId}/sessions/${data?.mostRecentAttendedSession}/details`]);
    }
  }

  openGroupDetails(user: any) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/group/${user?.groupInfo?.id}/dashboard`]);
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly userStatusColors = userStatusColors;
  protected readonly participantGroupStatusColors = participantGroupStatusColors;
}
